import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Button,
  Alert,
  Spinner,
  Badge,
} from '../shim/reactstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import Notification from '../entities/Notification';

// ─── Inbox tab ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 25;

const InboxTab = () => {
  const { apiClient } = useAuth();
  const { setUnreadCount } = useNotifications();
  const [notifications, setNotifications] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [markingId, setMarkingId] = useState(null);

  const load = useCallback(async (pageIndex) => {
    setLoadError('');
    try {
      const data = await apiClient.getInAppNotifications(pageIndex * PAGE_SIZE, PAGE_SIZE);
      setNotifications(data?.Items || []);
      setTotal(data?.TotalCount || 0);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load notifications.');
    }
  }, [apiClient]);

  useEffect(() => { load(page); }, [load, page]);

  const handleMarkRead = async (id) => {
    setMarkingId(id);
    try {
      await apiClient.markInAppNotificationRead(id);
      setNotifications(prev => prev.map(n => n.Id === id ? new Notification({ ...n, CreateTime: n.CreateTime, IsRead: true }) : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      setLoadError(err?.message || 'Failed to mark as read.');
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiClient.markAllInAppNotificationsRead();
      const unreadCount = (notifications || []).filter(n => !n.IsRead).length;
      setNotifications(prev => prev.map(n => new Notification({ ...n, CreateTime: n.CreateTime, IsRead: true })));
      setUnreadCount(prev => Math.max(0, prev - unreadCount));
    } catch (err) {
      setLoadError(err?.message || 'Failed to mark all as read.');
    }
  };

  const unreadCount = (notifications || []).filter(n => !n.IsRead).length;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      {loadError && <Alert color="danger">{loadError}</Alert>}

      {!notifications && !loadError && (
        <div className="text-center py-4">
          <Spinner />
        </div>
      )}

      {notifications && (
        <>
          {unreadCount > 0 && (
            <div className="d-flex justify-content-end mb-2">
              <Button size="sm" color="secondary" outline onClick={handleMarkAllRead}>
                Mark all as read
              </Button>
            </div>
          )}

          {notifications.length === 0 && (
            <p className="text-muted text-center py-3">No notifications.</p>
          )}

          {notifications.length > 0 && (
            <div className="d-flex flex-column gap-2">
              {notifications.map(n => (
                <div
                  key={n.Id}
                  className={`notification-block p-3 rounded border${n.IsRead ? ' notification-block--read' : ' notification-block--unread'}`}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className={`notification-block__read-icon${n.IsRead ? ' text-secondary' : ' text-primary'}`}
                            title={n.IsRead ? 'Read' : 'Unread'}
                            style={{ fontSize: '0.75rem' }}>
                        {n.IsRead ? '●' : '◉'}
                      </span>
                      <span className={`fw-semibold${n.IsRead ? ' text-secondary' : ''}`}>
                        {n.getDisplayTitle()}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {n.getFormattedCreateTime()}
                      </small>
                      {n.IsRead
                        ? <Badge color="secondary" pill className="small">Read</Badge>
                        : (
                          <Badge
                            color="primary"
                            pill
                            className="small"
                            style={{ cursor: markingId === n.Id ? 'wait' : 'pointer' }}
                            onClick={() => !markingId && handleMarkRead(n.Id)}
                            title="Click to mark as read"
                          >
                            {markingId === n.Id ? <Spinner size="sm" /> : 'Unread'}
                          </Badge>
                        )}
                    </div>
                  </div>
                  <p className={`mb-0 notification-block__text${n.IsRead ? ' text-muted' : ''}`}
                     style={{ fontSize: '0.875rem' }}
                     dangerouslySetInnerHTML={{ __html: n.getDisplayText() }} />
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </small>
              <div className="d-flex gap-1">
                <Button size="sm" outline disabled={page === 0} onClick={() => setPage(0)}>«</Button>
                <Button size="sm" outline disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</Button>
                <span className="px-2 d-flex align-items-center small">
                  Page {page + 1} of {totalPages}
                </span>
                <Button size="sm" outline disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</Button>
                <Button size="sm" outline disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

// ─── Notifications page ───────────────────────────────────────────────────────
export const Notifications = () => {
  return (
    <Container className="mt-4">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-9">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Notifications</h4>
              <Link to="/notifications/preferences" className="btn btn-outline-secondary btn-sm">
                Notification Preferences
              </Link>
            </CardHeader>
            <CardBody>
              <InboxTab />
            </CardBody>
          </Card>
        </div>
      </div>
    </Container>
  );
};

